import { useMemo, useState } from "react";
import { CardHeaderTag } from "components/shared/Tags/StatusTags";
import moment from "moment";
import { CalendarDays } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Loader } from "../Common/Loader";
import ShowToast from "../Common/Toast";
import SearchBarRed from "components/shared/SearchBar/SearchBarRed";
import Breadcrumb from "components/shared/Breadcrumb/Breadcrumb";
import Datepicker from "components/shared/Datepicker/Datepicker";
import TableWithFilters from "components/shared/TableWithFilters/TableWithFilters";
import FiltersPanel from "components/shared/TableWithFilters/FiltersPanel";
import FilterMultiSelect from "components/shared/TableWithFilters/FilterMultiSelect";
import { FilterLabel } from "components/shared/TableWithFilters/styles.tableWithFilters";
import FilterDateTrigger from "../PO/PurchaseOrderList/FilterDateTrigger";
import {
  StateContainer,
  StateImage,
  StateMessage,
  StateTitle,
  StateButton,
} from "../PO/PurchaseOrderList/styles";
import { DesktopPage, Title, TitleRow } from "components/core/Common/DesktopHelpers/DesktopHelpers";
import {
  PoIdLink,
  CellText,
} from "../PO/PurchaseOrderList/styles.purchaseOrderListDesktop";
import { strings } from "utils/constants/strings";
import { GRN_OR_FILTERS } from "./utils";
import { FilterFieldFixed, GrnStatusTags, IconCell, StateBox } from "./styles.grnDesktop";

const STATUS_OPTIONS = GRN_OR_FILTERS.map((filter) => ({ value: filter.value, label: filter.label }));

const DateCell = ({ value }) => (
  <IconCell>
    <CalendarDays size={16} />
    <CellText>{value}</CellText>
  </IconCell>
);

const GrnListDesktop = ({
  rows,
  isListLoading,
  isEmpty,
  hasError,
  isSearching,
  showToast,
  searchString,
  setSearchString,
  pageNumber,
  hasNextPage,
  goToPage,
  draftOrFilters,
  toggleDraftFilter,
  draftGrnDate,
  setDraftGrnDate,
  hasPendingFilterChanges,
  applyFilters,
  clearFilters,
  refetch,
}) => {
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);

  const columns = useMemo(
    () => [
      {
        id: "grn_id",
        header: strings.grnListDesktopGrnId,
        meta: { width: 240 },
        cell: (info) => (
          <PoIdLink>
            {info.row.original.grnNumber}
          </PoIdLink>
        ),
      },
      {
        id: "invoice_number",
        header: strings.grnListDesktopInvoiceNumber,
        meta: { width: 200 },
        cell: (info) => <CellText>{info.row.original.invoiceNumber}</CellText>,
      },
      {
        id: "grn_amount",
        header: strings.grnListDesktopGrnAmount,
        meta: { width: 140 },
        cell: (info) => <CellText>{info.row.original.grnAmount}</CellText>,
      },
      {
        id: "invoice_amount",
        header: strings.grnListDesktopInvoiceAmount,
        meta: { width: 160 },
        cell: (info) => <CellText>{info.row.original.invoiceAmount}</CellText>,
      },
      {
        id: "grn_date",
        header: strings.grnListDesktopGrnDate,
        meta: { width: 160 },
        cell: (info) => <DateCell value={info.row.original.grnDate} />,
      },
      {
        id: "payment_date",
        header: strings.grnListDesktopPaymentDate,
        meta: { width: 160 },
        cell: (info) => <DateCell value={info.row.original.paymentDate} />,
      },
      {
        id: "status",
        header: strings.grnListDesktopStatus,
        meta: { width: 240 },
        cell: (info) => (
          <GrnStatusTags>
            {info.row.original.tags.map((tag) => (
              <CardHeaderTag
                key={tag.text}
                text={tag.text}
                bgColor={tag.bg}
                textColor={tag.color}
                allSidesRounded
              />
            ))}
          </GrnStatusTags>
        ),
      },
    ],
    []
  );

  const filters = (
    <FiltersPanel onApply={applyFilters} onClear={clearFilters} applyDisabled={!hasPendingFilterChanges}>
      <FilterFieldFixed>
        <FilterLabel>{strings.grnListDesktopStatusFilter}</FilterLabel>
        <FilterMultiSelect
          options={STATUS_OPTIONS}
          selected={draftOrFilters}
          onToggle={toggleDraftFilter}
          placeholder="Select status"
        />
      </FilterFieldFixed>
      <FilterFieldFixed>
        <FilterLabel>{strings.grnListDesktopGrnDateFilter}</FilterLabel>
        <Datepicker
          selected={draftGrnDate}
          onChange={(date) => {
            setDraftGrnDate(date);
            setPickerOpen(false);
          }}
          open={pickerOpen}
          onInputClick={() => setPickerOpen(true)}
          onClickOutside={() => setPickerOpen(false)}
          customInput={
            <FilterDateTrigger value={draftGrnDate ? moment(draftGrnDate).format("DD MMM 'YY") : ""} />
          }
        />
      </FilterFieldFixed>
    </FiltersPanel>
  );

  const showState = hasError || isListLoading || isEmpty || rows.length === 0;

  const renderState = () => {
    if (isListLoading) return <Loader />;
    if (hasError) {
      return (
        <>
          <StateTitle>{strings.grnListDesktopUnableToLoad}</StateTitle>
          <StateMessage>{strings.grnListDesktopConnectionError}</StateMessage>
          <StateButton type="button" onClick={refetch}>
            {strings.grnListDesktopTryAgain}
          </StateButton>
        </>
      );
    }
    return (
      <>
        <StateImage src="/logos/po_empty.svg" alt="" />
        <StateTitle>{isSearching ? strings.grnListDesktopNoMatchingGrns : strings.grnListDesktopNoGrns}</StateTitle>
        <StateMessage>{strings.noGrnsFound}</StateMessage>
      </>
    );
  };

  return (
    <DesktopPage>
      <Breadcrumb items={[{ label: strings.grnDesktopBreadcrumbHome }, { label: strings.grnDesktopBreadcrumbGrns }]} />
      <TitleRow>
        <Title>{strings.grnListDesktopTitle}</Title>
        <SearchBarRed
          placeholder={strings.grnListDesktopSearchPlaceholder}
          value={searchString}
          onChange={(event) => setSearchString(event.target.value)}
        />
      </TitleRow>
      {showState ? (
        <>
          {filters}
          <StateBox>
            <StateContainer>{renderState()}</StateContainer>
          </StateBox>
        </>
      ) : (
        <TableWithFilters
          filters={filters}
          columns={columns}
          data={rows}
          getRowId={(row) => String(row.id)}
          isLoading={false}
          emptyMessage={strings.grnListDesktopEmptyMessage}
          page={pageNumber}
          hasNextPage={hasNextPage}
          onPageChange={goToPage}
          onRowClick={(row) => navigate(`/grn/details/list/${row.purchaseOrderId}/${row.id}`)}
        />
      )}
      {showToast ? <ShowToast /> : null}
    </DesktopPage>
  );
};

export default GrnListDesktop;
